import React, { createContext, useContext, useState, useEffect } from 'react';
import { activeClient } from '../config';

const DataContext = createContext(null);

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const DataProvider = ({ children, clientConfig = activeClient }) => {
  const [geojson, setGeojson] = useState(null);
  const [percentageData, setPercentageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Extract zone names from GeoJSON
  const [zoneNames, setZoneNames] = useState([]);

  // Extract available day types and time slots from percentage data
  const [dayTypes, setDayTypes] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);

  // Client display config (map center, zoom, etc.)
  const [clientDisplayConfig, setClientDisplayConfig] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);

      try {
        const fetchJSON = async (url) => {
          const res = await fetch(url);
          const contentType = res.headers.get('content-type') || '';
          if (!res.ok || !contentType.includes('application/json')) {
            throw new Error(`Failed to load ${url}: ${res.status}`);
          }
          return res.json();
        };

        // Load GeoJSON, percentage data, and client config in parallel
        const [geojsonData, percentageDataRaw, configData] = await Promise.all([
          fetchJSON(clientConfig.data.geojson),
          fetchJSON(clientConfig.data.percentages),
          fetchJSON(clientConfig.data.config),
        ]);

        setGeojson(geojsonData);
        setPercentageData(percentageDataRaw);
        setClientDisplayConfig(configData);

        // Extract zone names from GeoJSON features
        const names = geojsonData.features
          .map((feature) => feature.properties.name)
          .filter(Boolean)
          .sort();
        setZoneNames([...new Set(names)]);

        // Extract day types and time slots from percentage data
        const types = Object.keys(percentageDataRaw);
        setDayTypes(types);

        if (types.length > 0) {
          const slots = Object.keys(percentageDataRaw[types[0]]).sort();
          setTimeSlots(['Intera giornata', ...slots]);
        }
      } catch (err) {
        console.error('Error loading data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [clientConfig]);

  const value = {
    geojson,
    percentageData,
    zoneNames,
    dayTypes,
    timeSlots,
    loading,
    error,
    clientConfig: {
      ...clientConfig,
      ...(clientDisplayConfig || {}),
    },
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};

export default DataContext;
